/**
 * Utility functions for calculating date ranges used in date picker shortcuts
 * Using date-fns for better performance and consistency
 */

import {
  endOfDay,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subQuarters,
  subYears,
} from 'date-fns'

/**
 * Calculate date ranges for various shortcuts
 */
export const dateRangeCalculators = {
  // Specific days
  today: () => {
    const today = new Date()
    return {
      startDate: startOfDay(today),
      endDate: endOfDay(today),
    }
  },

  yesterday: () => {
    const yesterday = subDays(new Date(), 1)
    return {
      startDate: startOfDay(yesterday),
      endDate: endOfDay(yesterday),
    }
  },

  // Last N days (including today)
  last7Days: () => {
    const today = new Date()
    const sevenDaysAgo = subDays(today, 6)
    return {
      startDate: startOfDay(sevenDaysAgo),
      endDate: endOfDay(today),
    }
  },

  last14Days: () => {
    const today = new Date()
    const fourteenDaysAgo = subDays(today, 13)
    return {
      startDate: startOfDay(fourteenDaysAgo),
      endDate: endOfDay(today),
    }
  },

  last30Days: () => {
    const today = new Date()
    const thirtyDaysAgo = subDays(today, 29)
    return {
      startDate: startOfDay(thirtyDaysAgo),
      endDate: endOfDay(today),
    }
  },

  last90Days: () => {
    const today = new Date()
    const ninetyDaysAgo = subDays(today, 89)
    return {
      startDate: startOfDay(ninetyDaysAgo),
      endDate: endOfDay(today),
    }
  },

  last365Days: () => {
    const today = new Date()
    const yearAgo = subDays(today, 364)
    return {
      startDate: startOfDay(yearAgo),
      endDate: endOfDay(today),
    }
  },

  last12Months: () => {
    const today = new Date()
    const twelveMonthsAgo = subMonths(today, 12)
    return {
      startDate: startOfDay(twelveMonthsAgo),
      endDate: endOfDay(today),
    }
  },

  // Last complete periods
  lastWeek: () => {
    const today = new Date()
    const lastWeekDate = subDays(today, 7)

    return {
      startDate: startOfWeek(lastWeekDate, { weekStartsOn: 1 }), // Monday start
      endDate: endOfWeek(lastWeekDate, { weekStartsOn: 1 }),
    }
  },

  lastMonth: () => {
    const today = new Date()
    const lastMonth = subMonths(today, 1)

    return {
      startDate: startOfMonth(lastMonth),
      endDate: endOfMonth(lastMonth),
    }
  },

  lastQuarter: () => {
    const today = new Date()
    const lastQuarter = subQuarters(today, 1)

    return {
      startDate: startOfQuarter(lastQuarter),
      endDate: endOfQuarter(lastQuarter),
    }
  },

  lastYear: () => {
    const today = new Date()
    const lastYear = subYears(today, 1)

    return {
      startDate: startOfYear(lastYear),
      endDate: endOfYear(lastYear),
    }
  },

  // To date periods
  weekToDate: () => {
    const today = new Date()
    return {
      startDate: startOfWeek(today, { weekStartsOn: 1 }), // Monday start
      endDate: endOfDay(today),
    }
  },

  monthToDate: () => {
    const today = new Date()
    return {
      startDate: startOfMonth(today),
      endDate: endOfDay(today),
    }
  },

  quarterToDate: () => {
    const today = new Date()
    return {
      startDate: startOfQuarter(today),
      endDate: endOfDay(today),
    }
  },

  yearToDate: () => {
    const today = new Date()
    return {
      startDate: startOfYear(today),
      endDate: endOfDay(today),
    }
  },
}

/**
 * Modular shortcut groups for reusability
 */
export const quickShortcuts = [
  { label: 'Today', key: 'today', date: () => dateRangeCalculators.today() },
  { label: 'Yesterday', key: 'yesterday', date: () => dateRangeCalculators.yesterday() },
]

export const lastNDaysShortcuts = [
  { label: 'Last 7 days', key: 'last7Days', date: () => dateRangeCalculators.last7Days() },
  { label: 'Last 14 days', key: 'last14Days', date: () => dateRangeCalculators.last14Days() },
  { label: 'Last 30 days', key: 'last30Days', date: () => dateRangeCalculators.last30Days() },
  { label: 'Last 90 days', key: 'last90Days', date: () => dateRangeCalculators.last90Days() },
  { label: 'Last 365 days', key: 'last365Days', date: () => dateRangeCalculators.last365Days() },
  { label: 'Last 12 months', key: 'last12Months', date: () => dateRangeCalculators.last12Months() },
]

export const lastCompleteShortcuts = [
  { label: 'Last week', key: 'lastWeek', date: () => dateRangeCalculators.lastWeek() },
  { label: 'Last month', key: 'lastMonth', date: () => dateRangeCalculators.lastMonth() },
  { label: 'Last quarter', key: 'lastQuarter', date: () => dateRangeCalculators.lastQuarter() },
  { label: 'Last year', key: 'lastYear', date: () => dateRangeCalculators.lastYear() },
]

export const toDateShortcuts = [
  { label: 'Week to date', key: 'weekToDate', date: () => dateRangeCalculators.weekToDate() },
  { label: 'Month to date', key: 'monthToDate', date: () => dateRangeCalculators.monthToDate() },
  { label: 'Quarter to date', key: 'quarterToDate', date: () => dateRangeCalculators.quarterToDate() },
  { label: 'Year to date', key: 'yearToDate', date: () => dateRangeCalculators.yearToDate() },
]

/**
 * Default shortcuts grouped by category
 * Users can import individual shortcut groups or use this complete default set
 */
export const defaultShortcuts = [
  {
    title: 'Quick',
    shortcuts: quickShortcuts,
  },
  {
    title: 'Last N Days',
    shortcuts: lastNDaysShortcuts,
  },
  {
    title: 'Last Complete',
    shortcuts: lastCompleteShortcuts,
  },
  {
    title: 'To Date',
    shortcuts: toDateShortcuts,
  },
]

// Keep the old export name for backward compatibility
export const dateRangeShortcuts = defaultShortcuts
